import type { ReportShareState } from "@hydeck/mdt-contracts";
import { createEffect, createResource, createSignal, For, Show } from "solid-js";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";

type ShareReportSectionProps = {
  endpoint: string;
  reportType: "reports" | "incidents";
  permissionScope?: string;
  label?: string;
  onPublicChange?: (value: boolean) => void;
};

const expiryOptions = [
  { label: "1 hour", hours: 1 },
  { label: "6 hours", hours: 6 },
  { label: "1 day", hours: 24 },
  { label: "1 week", hours: 168 },
  { label: "2 weeks", hours: 336 },
  { label: "1 month", hours: 720 },
  { label: "Never", hours: -1 },
];

const emptyShares = (): ReportShareState => ({ forPublic: false, shared: [] });

export function ShareReportSection(props: ShareReportSectionProps) {
  const [revision, setRevision] = createSignal(0);
  const [shares, setShares] = createSignal<ReportShareState>(emptyShares());
  const [open, setOpen] = createSignal(false);
  const [forPublic, setForPublic] = createSignal(false);
  const [sharedUserId, setSharedUserId] = createSignal("");
  const [expiresHours, setExpiresHours] = createSignal(168);
  const [saving, setSaving] = createSignal(false);
  const [status, setStatus] = createSignal("");

  const canShare = () =>
    hasPermission(`${props.reportType}.share` as "reports.share" | "incidents.share", props.permissionScope);

  const [loaded] = createResource(
    () => revision(),
    () =>
      apiRequest<ReportShareState>(props.endpoint, {
        token: currentUser()?.accessToken,
      }),
  );

  createEffect(() => {
    const value = loaded();
    if (value) {
      setShares({ forPublic: value.forPublic, shared: value.shared ?? [] });
      props.onPublicChange?.(value.forPublic);
    }
  });

  const openShare = () => {
    setForPublic(shares().forPublic);
    setSharedUserId("");
    setExpiresHours(168);
    setStatus("");
    setOpen(true);
  };

  const share = async () => {
    const userId = sharedUserId().trim();
    if (!forPublic() && !userId) {
      setStatus("Choose public sharing or enter a user ID.");
      return;
    }
    setSaving(true);
    setStatus("");
    try {
      const hours = expiresHours();
      const expiresAt =
        hours < 0
          ? null
          : new Date(Date.now() + hours * 60 * 60 * 1000)
              .toISOString()
              .replace("T", " ")
              .replace("Z", "");
      const next = await apiRequest<ReportShareState>(props.endpoint, {
        method: "PATCH",
        token: currentUser()?.accessToken,
        body: {
          forPublic: forPublic(),
          sharedUserId: userId ? Number(userId) : null,
          expiresAt,
        },
      });
      setShares(next);
      props.onPublicChange?.(next.forPublic);
      setOpen(false);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not share record");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (target: "public" | number) => {
    try {
      const next = await apiRequest<ReportShareState>(props.endpoint, {
        method: "DELETE",
        token: currentUser()?.accessToken,
        query: target === "public" ? { public: true } : { userId: target },
      });
      setShares(next);
      props.onPublicChange?.(next.forPublic);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not revoke share");
    }
  };

  return (
    <section class="mt-4 flex flex-col gap-3 border-t border-[rgba(255,255,255,.12)] pt-4">
      <div class="flex items-center justify-between pl-4">
        <div>
          <p class="text-cyan">{props.label ?? "Share report"}</p>
          <p class="text-xs text-white-55">Control public and user access to this record.</p>
        </div>
        <Show when={canShare()}>
          <button
            class="flex h-8 items-center gap-2 bg-green-gradient px-3 text-sm text-white hover:drop-shadow-green-gradient"
            onClick={openShare}
          >
            <span class="text-lg leading-none">+</span> Share
          </button>
        </Show>
      </div>

      <div class="flex flex-wrap gap-2 pl-4">
        <Show when={shares().forPublic}>
          <span class="flex items-center gap-2 bg-[rgba(0,248,185,.18)] px-3 py-2 text-sm text-cyan">
            Publicly shared
            <Show when={canShare()}>
              <button aria-label="Revoke public share" onClick={() => void revoke("public")}>×</button>
            </Show>
          </span>
        </Show>
        <For each={shares().shared}>
          {(userId) => (
            <span class="flex items-center gap-2 bg-[rgba(255,255,255,.11)] px-3 py-2 text-sm">
              User #{userId}
              <Show when={canShare()}>
                <button aria-label={`Revoke user ${userId} share`} onClick={() => void revoke(userId)}>×</button>
              </Show>
            </span>
          )}
        </For>
        <Show when={!shares().forPublic && shares().shared.length === 0 && !loaded.loading}>
          <p class="pl-1 text-sm text-white-55">This record is not shared.</p>
        </Show>
      </div>

      <Show when={open()}>
        <div class="border border-[rgba(255,255,255,.15)] bg-[rgba(15,19,31,.96)] p-4 shadow-lg">
          <div class="mb-3 flex items-center justify-between">
            <p class="text-lg text-white">Share report</p>
            <button class="text-white-55" onClick={() => setOpen(false)}>×</button>
          </div>
          <label class="mb-3 flex items-center gap-3 text-sm">
            <input type="checkbox" checked={forPublic()} onChange={(event) => setForPublic(event.currentTarget.checked)} />
            <span>Share for public</span>
          </label>
          <label class="mb-3 flex flex-col gap-1 text-sm">
            <span>Expires</span>
            <select
              class="bg-[rgba(255,255,255,.11)] px-3 py-2 text-white"
              value={expiresHours()}
              onChange={(event) => setExpiresHours(Number(event.currentTarget.value))}
            >
              <For each={expiryOptions}>
                {(option) => <option class="text-black" value={option.hours}>{option.label}</option>}
              </For>
            </select>
          </label>
          <label class="mb-3 flex flex-col gap-1 text-sm">
            <span>Shared to (optional)</span>
            <input
              class="bg-[rgba(255,255,255,.11)] px-3 py-2 text-white"
              type="number"
              min="1"
              value={sharedUserId()}
              placeholder="MDT user ID"
              onInput={(event) => setSharedUserId(event.currentTarget.value)}
            />
          </label>
          <Show when={status()}><p class="mb-3 text-sm text-red-300">{status()}</p></Show>
          <button
            class="w-full bg-green-gradient px-3 py-2 text-white disabled:opacity-50"
            disabled={saving()}
            onClick={() => void share()}
          >
            {saving() ? "Sharing..." : "Share"}
          </button>
        </div>
      </Show>
    </section>
  );
}
